import { useEffect, useState } from "react";
import { api, ApiError } from "./api/client";
import { DashboardData, MeResponse } from "./types";
import { PaymentsPage } from "./pages/PaymentsPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { AgentsPage } from "./pages/AgentsPage";
import { ReturnsPage } from "./pages/ReturnsPage";
import { SubcontractorsPage } from "./pages/SubcontractorsPage";
import { BackfillPage } from "./pages/BackfillPage";
import { KeyAnalyticPage } from "./pages/KeyAnalyticPage";
import { ClientCostPage } from "./pages/ClientCostPage";
import { DealsRegistryPage } from "./pages/DealsRegistryPage";
import { ExportImportPage } from "./pages/ExportImportPage";
import { CloseoutPage } from "./pages/CloseoutPage";
import { TestCashPositionPage } from "./pages/TestCashPositionPage";
import { AuditPage } from "./pages/AuditPage";
import { FxPage, ExchangeView, type Section as FxSection } from "./pages/FxPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TasksPage } from "./pages/TasksPage";
import { MorePage } from "./pages/MorePage";
import { HealthPage } from "./pages/HealthPage";
import { SearchPage } from "./pages/SearchPage";
import { RubInflowPage } from "./pages/RubInflowPage";
import { TabBar, TabKey, SECONDARY_TABS } from "./components/TabBar";
import { useBackButton } from "./components/telegram";
import { Onboarding, onboardingNeeded } from "./components/Onboarding";
import {
  supportsFullscreen,
  enterFullscreen,
  exitFullscreen,
  isFullscreenNow,
} from "./auth";

export function App() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("tasks");
  // Раздел FX, на который надо открыть страницу «Экспорт» (например, при
  // переходе из реестра платежей сразу в «Распределение»). Сбрасывается при
  // обычном переключении вкладок снизу.
  const [fxSection, setFxSection] = useState<FxSection | undefined>(undefined);
  const [taskCount, setTaskCount] = useState(0);

  // Обычная навигация снизу: всегда открывает FX с дефолтного раздела.
  const navigate = (t: TabKey) => {
    setFxSection(undefined);
    setTab(t);
  };
  // Программный переход в «Экспорт → Распределение».
  const goToFxDistribute = () => {
    setFxSection("distribute");
    setTab("fx");
  };
  const [showOnboarding, setShowOnboarding] = useState(onboardingNeeded());
  const [fullscreen, setFullscreen] = useState(isFullscreenNow());
  const fsSupported = supportsFullscreen();

  // Следим за сменой полноэкранного режима (кнопка Telegram / системный жест).
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg?.onEvent) return;
    const onChange = () => setFullscreen(isFullscreenNow());
    tg.onEvent("fullscreenChanged", onChange);
    return () => tg.offEvent?.("fullscreenChanged", onChange);
  }, []);

  useEffect(() => {
    api
      .get<MeResponse>("/api/me")
      .then(setMe)
      .catch((e: ApiError) => setError(e.message));
  }, []);

  // Счётчик задач для бейджа на вкладке — обновляем при смене вкладки.
  useEffect(() => {
    api
      .get<DashboardData>("/api/tasks/dashboard")
      .then((d) => setTaskCount(d.open_tasks))
      .catch(() => {});
  }, [tab]);

  // На вторичных разделах («Аналитика»/«Аудит»/«Профиль») показываем
  // нативную кнопку «назад» Telegram — возврат в меню «Ещё».
  useBackButton(() => setTab("more"), SECONDARY_TABS.includes(tab));

  if (error) {
    return (
      <div className="min-h-full p-4 flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="text-3xl mb-2">🔒</div>
          <div className="text-tg-text font-medium">Доступ закрыт</div>
          <div className="text-tg-hint mt-2 text-sm">{error}</div>
          <div className="text-tg-hint mt-4 text-xs">
            Откройте mini-app из бота через меню — без подписи Telegram
            доступ невозможен.
          </div>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-tg-hint animate-pulse">Загрузка…</div>
      </div>
    );
  }

  // Раньше «чистый» Key analytic видел ТОЛЬКО раздел обезличенной
  // аналитики. По запросу клиента — теперь видит все разделы, как
  // админ; данные обезличиваются сервером (middleware). На каждом
  // экране показываем плашку «🔒 Обезличенный режим».

  return (
    <>
    {showOnboarding && (
      <Onboarding me={me} onDone={() => setShowOnboarding(false)} />
    )}
    <div
      className="min-h-full bg-tg-bg text-tg-text"
      // Нижний отступ перекрывает фиксированную панель навигации вместе
      // с вырезом под home-indicator на iPhone.
      style={{
        paddingBottom: "calc(env(safe-area-inset-bottom) + 4.75rem)",
      }}
    >
      <header
        className="sticky top-0 z-10 bg-tg-headerBg border-b border-tg-secondaryBg px-4 pb-3"
        // Верхний отступ учитывает вырез/«чёлку» (iPhone) и, в полноэкранном
        // режиме, зону под системными контролами Telegram (content safe-area).
        style={{
          paddingTop:
            "calc(env(safe-area-inset-top) + var(--tg-content-safe-area-inset-top, 0px) + 0.75rem)",
        }}
      >
        <div className="max-w-3xl mx-auto flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm text-tg-hint">deals-fix-bot</div>
            <div className="text-base font-semibold truncate">
              {me.display_name || `tg #${me.tg_id}`}
            </div>
            <div className="text-xs text-tg-hint truncate">
              Роли: {me.roles.length ? me.roles.join(", ") : "—"}
            </div>
            {me.permissions.key_analytic_only && (
              <div className="mt-1 inline-block px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 text-[11px] font-medium">
                🔒 Обезличенный режим — имена скрыты кодами
              </div>
            )}
          </div>
          {fsSupported && (
            <button
              onClick={() => (fullscreen ? exitFullscreen() : enterFullscreen())}
              className="shrink-0 mt-0.5 px-2.5 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
              title={fullscreen ? "Свернуть" : "На весь экран"}
            >
              {fullscreen ? "🗗 Свернуть" : "⛶ Во весь экран"}
            </button>
          )}
        </div>
        {!fsSupported && (
          <div className="max-w-3xl mx-auto mt-1 text-[11px] text-tg-hint">
            Доступен полноэкранный режим — обновите Telegram, чтобы открывать
            приложение во весь экран.
          </div>
        )}
      </header>

      <main className="max-w-3xl mx-auto p-4">
        {tab === "tasks" && <TasksPage onNavigate={setTab} />}
        {tab === "payments" && me.permissions.payments_view && (
          <PaymentsPage me={me} onGoToDistribute={goToFxDistribute} />
        )}
        {tab === "deals" && me.permissions.payments_view && (
          <DealsRegistryPage me={me} />
        )}
        {tab === "trash" && me.roles.includes("admin") && (
          <DealsRegistryPage me={me} trashed />
        )}
        {tab === "exports_import" && (me.roles.includes("admin") || me.roles.includes("finance_lead")) && (
          <ExportImportPage me={me} />
        )}
        {tab === "closeout" && me.roles.includes("admin") && (
          <CloseoutPage me={me} />
        )}
        {tab === "fx" && me.permissions.fx_view && (
          <FxPage me={me} initialSection={fxSection} />
        )}
        {tab === "exchange" && me.permissions.fx_view && <ExchangeView />}
        {tab === "analytics" && me.permissions.analytics && (
          <AnalyticsPage me={me} />
        )}
        {tab === "agents" && me.permissions.agents_view && <AgentsPage me={me} />}
        {tab === "returns" && me.permissions.returns_view && <ReturnsPage me={me} />}
        {tab === "subcontractors" && me.permissions.subcontractors_view && (
          <SubcontractorsPage me={me} />
        )}
        {tab === "backfill" && me.permissions.backfill_view && <BackfillPage me={me} />}
        {tab === "key_analytics" && me.permissions.key_analytics && (
          <KeyAnalyticPage me={me} />
        )}
        {tab === "client_cost" && me.permissions.client_cost && (
          <ClientCostPage me={me} />
        )}
        {tab === "audit" && me.permissions.audit && <AuditPage me={me} />}
        {tab === "settings" && <SettingsPage me={me} />}
        {tab === "search" && <SearchPage me={me} />}
        {tab === "rubinflow" && me.permissions.payments_view && (
          <RubInflowPage me={me} />
        )}
        {tab === "test_cash_position" && me.permissions.payments_view && (
          <TestCashPositionPage me={me} />
        )}
        {tab === "health" && me.roles.includes("admin") && <HealthPage />}
        {tab === "more" && <MorePage me={me} onNavigate={setTab} />}
      </main>

      <TabBar
        current={tab}
        onChange={navigate}
        permissions={me.permissions}
        badges={{ tasks: taskCount }}
      />
    </div>
    </>
  );
}
